import * as THREE from "three";
import { glslFloat, NAP_SHADE_MAX, NAP_SHADE_MIN } from "./pathTracingNap.js";

/**
 * Filament / Three.js / path-tracer anisotropy map packing:
 *   R, G = tangent-space nap direction in [0, 1]  (0.5 is 0; 1 is +1)
 *   B     = strength in [0, 1]
 *   A     = 255
 * Direction (1, 0) is along +U (the weave tangent). The map must be seamless —
 * it tiles with the Heim albedo (repeat 5×5).
 */

export const ANISO_MAP_SIZE = 1024;

// Parked: Heim/Dahlia/Mollia anisotropyMap files and materials.json paths stay.
// Flip to true to load and apply them again (WebGL only; path tracing still strips).
export const ENABLE_ANISOTROPY_MAPS = false;

// One field for Heim, Dahlia and Mollia. The generator writes a copy per group
// so later recipe changes land on all three files in one pass.
export const ANISOTROPY_GROUPS = Object.freeze(["heim", "dahlia", "mollia"]);
export const ANISOTROPY_FIELD = Object.freeze({
  seed: 1,
  periodU: 1,
  periodV: 4,
  octaves: 1,
  // Integer shear (-1, 4) tiles and slants the thick bands atan(1/4) ≈ 14°.
  // maxAngle stays large so pink/mint contrast still reads the diagonal.
  shearU: -1,
  baseAngle: Math.atan2(1, 4),
  maxAngle: Math.PI / 2.2,
});

export function encodeAnisotropy(dirX, dirY, strength = 1) {
  return [
    Math.round((dirX * 0.5 + 0.5) * 255),
    Math.round((dirY * 0.5 + 0.5) * 255),
    Math.round(Math.min(1, Math.max(0, strength)) * 255),
    255,
  ];
}

export function decodeAnisotropy(r, g, b) {
  const x = (r / 255) * 2 - 1;
  const y = (g / 255) * 2 - 1;
  const len = Math.hypot(x, y) || 1;
  return { x: x / len, y: y / len, strength: b / 255 };
}

export function configureAnisotropyMap(texture) {
  if (!texture) return texture;
  texture.colorSpace = THREE.NoColorSpace;
  texture.flipY = false;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

function wrap01(t) {
  return ((t % 1) + 1) % 1;
}

function quintic(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Tileable value noise. `periodU`/`periodV` are integer feature counts per tile. */
export function tileableValueNoise(u, v, periodU, seed = 0, periodV = periodU) {
  const pu = Math.max(1, periodU | 0);
  const pv = Math.max(1, periodV | 0);
  const x = wrap01(u) * pu;
  const y = wrap01(v) * pv;
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const fx = x - x0;
  const fy = y - y0;
  const x1 = (x0 + 1) % pu;
  const y1 = (y0 + 1) % pv;
  const sx = quintic(fx);
  const sy = quintic(fy);
  const h = (ix, iy) => {
    const n = Math.sin(ix * 127.1 + iy * 311.7 + seed * 74.7) * 43758.5453;
    return n - Math.floor(n);
  };
  const a = h(x0, y0);
  const b = h(x1, y0);
  const c = h(x0, y1);
  const d = h(x1, y1);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

export function tileableFbm(u, v, period, octaves, seed = 0, periodV = period) {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let pU = Math.max(1, period | 0);
  let pV = Math.max(1, periodV | 0);
  for (let i = 0; i < octaves; i++) {
    sum += amp * tileableValueNoise(u, v, pU, seed + i * 19, pV);
    norm += amp;
    amp *= 0.5;
    pU *= 2;
    pV *= 2;
  }
  return sum / (norm || 1);
}

function tileableFbmGrad(u, v, period, octaves, seed, e = 0.06) {
  const nL = tileableFbm(u - e, v, period, octaves, seed);
  const nR = tileableFbm(u + e, v, period, octaves, seed);
  const nD = tileableFbm(u, v - e, period, octaves, seed);
  const nU = tileableFbm(u, v + e, period, octaves, seed);
  return { du: (nR - nL) / (2 * e), dv: (nU - nD) / (2 * e) };
}

/**
 * Baked nap: thick bands from a period-1 wave on an integer lattice
 * `t = shearU*u + periodV*v` (tiles, leans atan(|shearU|/periodV) ≈ 14°).
 * Value-noise FBM cannot live on that axis at period 1 (one lattice point
 * is constant). A 2×2 FBM warps the phase so the slashes are not perfect sines.
 */
export function fillSeamlessAnisotropy(data, width, height, options = {}) {
  const seed = options.seed ?? ANISOTROPY_FIELD.seed;
  const periodU = options.periodU ?? ANISOTROPY_FIELD.periodU;
  const periodV = options.periodV ?? ANISOTROPY_FIELD.periodV;
  const octaves = options.octaves ?? ANISOTROPY_FIELD.octaves;
  const shearU = options.shearU ?? ANISOTROPY_FIELD.shearU;
  const baseAngle = options.baseAngle ?? ANISOTROPY_FIELD.baseAngle;
  const maxAngle = options.maxAngle ?? ANISOTROPY_FIELD.maxAngle;
  const wobblePeriod = Math.max(2, periodU * 2);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const u = (x + 0.5) / width;
      const v = (y + 0.5) / height;
      const t = shearU * u + periodV * v;
      const wobble = tileableFbm(u, v, wobblePeriod, octaves, seed, 2) * 2 - 1;
      const n1 = Math.sin(2 * Math.PI * t + 0.45 * wobble);
      const n2 = Math.sin(2 * Math.PI * t + seed + 0.2 * wobble);
      const angle = baseAngle + (0.75 * n1 + 0.25 * n2) * maxAngle;
      const dx = Math.cos(angle);
      const dy = Math.sin(angle);
      const str = 0.88 + 0.12 * tileableFbm(u, v, wobblePeriod, octaves, seed + 22, 2);
      const [r, g, b, a] = encodeAnisotropy(dx, dy, str);
      const i = (y * width + x) * 4;
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = a;
    }
  }
  return data;
}

export function createSeamlessAnisotropyMap(size = ANISO_MAP_SIZE) {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d");
  const image = ctx.createImageData(size, size);
  fillSeamlessAnisotropy(image.data, size, size);
  ctx.putImageData(image, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  return configureAnisotropyMap(texture);
}

export function ensureTangents(geometry) {
  if (!geometry?.attributes?.position || !geometry.attributes.uv) return false;
  if (geometry.attributes.tangent) return true;
  if (!geometry.attributes.normal) geometry.computeVertexNormals();
  if (!geometry.index) {
    const count = geometry.attributes.position.count;
    const idx = new Uint32Array(count);
    for (let i = 0; i < count; i++) idx[i] = i;
    geometry.setIndex(new THREE.BufferAttribute(idx, 1));
  }
  try {
    geometry.computeTangents();
    return true;
  } catch {
    return false;
  }
}

/**
 * UV-locked pile from anisotropyMap, plus a little grazing darken. Both only
 * darken vs the fabric. WebGL only; path tracing strips anisotropy maps.
 */
export function applyNapShading(material) {
  if (!material || !(material.anisotropy > 0)) return material;
  material.customProgramCacheKey = () => "heim-nap-v2";
  material.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <lights_physical_fragment>",
      `#include <lights_physical_fragment>
#ifdef USE_ANISOTROPY
	{
		vec3 napViewDir = normalize( vViewPosition );
		float alongT = abs( dot( napViewDir, tbn[ 0 ] ) );
		float alongB = abs( dot( napViewDir, tbn[ 1 ] ) );
		float napAlign = abs( alongT - alongB );
		float napGrazing = saturate( 1.2 - abs( dot( napViewDir, normal ) ) );
		float napMap = 0.5;
	#ifdef USE_ANISOTROPYMAP
		vec2 napRaw = texture2D( anisotropyMap, vAnisotropyMapUv ).rg * 2.0 - 1.0;
		float napLen = length( napRaw );
		if ( napLen > 1e-5 ) {
			napRaw /= napLen;
			napMap = smoothstep( 0.18, 0.82, saturate( napRaw.y * 0.5 + 0.5 ) );
		}
	#endif
		float napAmt = mix( napMap, max( napMap, napAlign * napGrazing ), 0.12 );
		float napShade = mix( ${glslFloat(NAP_SHADE_MAX)}, ${glslFloat(NAP_SHADE_MIN)}, napAmt );
		material.diffuseColor *= napShade;
		material.diffuseContribution *= napShade;
		material.specularColor *= napShade;
		material.specularColorBlended *= napShade;
	}
#endif
`,
    );
  };
  material.needsUpdate = true;
  return material;
}
